/**
 * The browser half of WebAuthn.
 *
 * The server issues a challenge carrying everything `navigator.credentials`
 * needs, and expects an assertion back in the same base64url shape it sent —
 * so this module is entirely about the encoding boundary between JSON and the
 * `ArrayBuffer`s the platform API insists on, and about nothing else. No
 * decisions are made here; the server verifies.
 *
 * Base64URL rather than base64 throughout, because these values travel in JSON
 * that is sometimes logged and sometimes put in a URL, and `+`/`/` survive
 * neither reliably.
 */

export function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

export function bytesToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export interface ServerPublicKeyRequest {
  readonly challenge: string;
  readonly rpId: string;
  readonly timeout: number;
  readonly userVerification: 'preferred';
  readonly allowCredentials: readonly { readonly type: 'public-key'; readonly id: string }[];
}

/**
 * The assertion in the shape the SERVER parses it — flat, and
 * `clientDataJson` rather than the platform's `clientDataJSON`.
 *
 * Deliberately not the browser's `PublicKeyCredential` shape. Forwarding that
 * verbatim is the obvious thing to do and it fails validation before any
 * cryptography runs, which locks out every account whose only factor is a
 * passkey — a failure that looks like a broken key rather than a wrong field
 * name. See `assertionSchema` in shared/schemas/security.ts.
 */
export interface WebauthnAssertion {
  readonly credentialId: string;
  readonly clientDataJson: string;
  readonly authenticatorData: string;
  readonly signature: string;
  readonly userHandle: string | null;
}

export function webauthnSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
}

/**
 * Prompts for the security key and returns what the server asked for.
 *
 * Returns null when the person dismisses the prompt or has no matching key —
 * a cancellation is a choice, not an error, and surfacing it as one would put
 * a red alert on a screen where somebody simply changed their mind. A genuine
 * failure still throws.
 */
export async function requestAssertion(publicKey: ServerPublicKeyRequest): Promise<WebauthnAssertion | null> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: base64UrlToBytes(publicKey.challenge) as BufferSource,
      rpId: publicKey.rpId,
      timeout: publicKey.timeout,
      userVerification: publicKey.userVerification,
      allowCredentials: publicKey.allowCredentials.map((credentialDescriptor) => ({
        type: 'public-key' as const,
        id: base64UrlToBytes(credentialDescriptor.id) as BufferSource,
      })),
    },
  })) as PublicKeyCredential | null;

  if (!credential) return null;
  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    credentialId: bytesToBase64Url(credential.rawId),
    clientDataJson: bytesToBase64Url(response.clientDataJSON),
    authenticatorData: bytesToBase64Url(response.authenticatorData),
    signature: bytesToBase64Url(response.signature),
    userHandle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
  };
}

/**
 * The registration options as the SERVER sends them — the same base64url
 * treatment as the assertion request, applied to `challenge` and to the user
 * handle.
 */
export interface ServerPublicKeyCreation {
  readonly challenge: string;
  readonly rp: { readonly id: string; readonly name: string };
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly pubKeyCredParams: readonly { readonly type: 'public-key'; readonly alg: number }[];
  readonly timeout: number;
  readonly attestation: 'none';
  readonly excludeCredentials: readonly { readonly type: 'public-key'; readonly id: string }[];
  readonly authenticatorSelection: { readonly userVerification: 'preferred'; readonly residentKey: 'preferred' };
}

/** What the server's registration endpoint parses. Flat, like the assertion. */
export interface WebauthnRegistration {
  readonly clientDataJson: string;
  readonly attestationObject: string;
  readonly transports?: string[];
}

/**
 * Creates a credential on the authenticator and returns what the server asked
 * for. Null on dismissal, for the same reason `requestAssertion` returns null.
 *
 * The user handle is encoded rather than sent as text: the server stores the
 * principal id there, WebAuthn wants bytes, and `TextEncoder` would produce
 * different bytes than the base64url the server will compare against.
 */
export async function createCredential(publicKey: ServerPublicKeyCreation): Promise<WebauthnRegistration | null> {
  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: base64UrlToBytes(publicKey.challenge) as BufferSource,
      rp: publicKey.rp,
      user: {
        id: new TextEncoder().encode(publicKey.user.id) as BufferSource,
        name: publicKey.user.name,
        displayName: publicKey.user.displayName,
      },
      pubKeyCredParams: publicKey.pubKeyCredParams.map((parameter) => ({ ...parameter })),
      timeout: publicKey.timeout,
      attestation: publicKey.attestation,
      excludeCredentials: publicKey.excludeCredentials.map((credentialDescriptor) => ({
        type: 'public-key' as const,
        id: base64UrlToBytes(credentialDescriptor.id) as BufferSource,
      })),
      authenticatorSelection: publicKey.authenticatorSelection,
    },
  })) as PublicKeyCredential | null;

  if (!credential) return null;
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports = typeof response.getTransports === 'function' ? response.getTransports() : undefined;

  return {
    clientDataJson: bytesToBase64Url(response.clientDataJSON),
    attestationObject: bytesToBase64Url(response.attestationObject),
    ...(transports && transports.length > 0 ? { transports } : {}),
  };
}

/** Whether a rejection was the person dismissing the platform prompt. */
export function wasDismissed(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}
